import { useEffect, useState, useSyncExternalStore } from "react";
import { subscribe, getSnapshot } from "@/lib/loadingStore";

const SHOW_DELAY_MS = 200;  // don't flash it for requests that finish almost instantly
const MIN_VISIBLE_MS = 350; // once shown, stay up long enough to actually read as "loading"

/** Centered, branded loading overlay. Mounted once in App.js and driven
 * entirely by the shared loading store — every call through lib/api.js
 * (and anything wrapped in withLoading()) shows it automatically, so
 * individual pages don't need to wire this up themselves. */
export default function GlobalLoader() {
  const active = useSyncExternalStore(subscribe, getSnapshot);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let showTimer, hideTimer, shownAt;

    if (active) {
      showTimer = setTimeout(() => {
        shownAt = Date.now();
        setVisible(true);
      }, SHOW_DELAY_MS);
    } else {
      clearTimeout(showTimer);
      setVisible((wasVisible) => {
        if (!wasVisible) return false;
        const elapsed = shownAt ? Date.now() - shownAt : MIN_VISIBLE_MS;
        const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
        hideTimer = setTimeout(() => setVisible(false), remaining);
        return wasVisible;
      });
    }

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!visible) return null;

  return (
    <div className="madio-loader-overlay" role="status" aria-live="polite" aria-label="Loading">
      <div className="madio-loader-panel">
        <div className="madio-loader-badge">
          <svg className="madio-loader-ring" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle className="madio-loader-ring-track" cx="32" cy="32" r="27" strokeWidth="4" />
            <circle className="madio-loader-ring-sweep" cx="32" cy="32" r="27" strokeWidth="4" />
          </svg>
          <span className="madio-loader-mark">M</span>
        </div>
        <span className="madio-loader-text">MADIO</span>
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}
